import { useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from "react";

import styles from "./Record.module.css";

/** The feed is never lower than this (design 2026-10-24 §6.1: „mindestens ≈ 420 px") — where the page scrolls. */
export const FEED_MIN_REM = 26;

/**
 * From this window height on the page does not scroll (Task 9, UX-Gutachten
 * 1, B-2): the grid of fact sheet and record is as tall as the window below
 * its top, and the feed takes what the panel leaves — one scrollbar, the
 * feed's. A height, not a width: it is measured here, with the room it
 * decides about, and is no breakpoint of the layout's widths (those stay the
 * four of `tokens.css`); the stylesheet holds the grid only where the two
 * columns stand (`width > 900px`).
 */
export const FIXED_FROM_PX = 760;

/** The grid is never lower than this — a window that leaves less lets the page scroll a little instead. */
const GRID_MIN_REM = 20;

/**
 * The feed's floor while the grid holds the window (fix round 1, ruling): a
 * tall intake result (up to 20 files) or an open note must not squeeze it to
 * nothing. Where the panel's other parts and this floor do not fit, the grid
 * lets go and the page scrolls — nothing is out of reach.
 */
export const FEED_FLOOR_REM = 12;

/** What a Tab can land on. An entry's own `tabIndex={-1}` is a place for focus, not a stop. */
const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * As tall as the room from the feed's top to the window's bottom edge, less
 * what stands below it — the panel's own padding and edge and the page's
 * bottom padding — and never under `FEED_MIN_REM`. Measured in the page's
 * coordinates (`scrollY`), so a page scrolled when it is measured gets the
 * height it has at its top. The panel's part is the panel's bottom less the
 * feed's: the same whatever height the feed is given, so writing it never
 * changes what the next measure finds.
 */
function feedHeight(feed: HTMLElement): number {
  const box = feed.getBoundingClientRect();
  const panel = feed.closest(`.${styles.record}`);
  const below = (panel !== null ? panel.getBoundingClientRect().bottom - box.bottom : 0) + pageBottom(feed);
  const room = document.documentElement.clientHeight - (box.top + window.scrollY) - below;
  return Math.round(Math.max(FEED_MIN_REM * rem(), room));
}

function rem(): number {
  return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}

/** The work area's bottom padding: what stands under the record at the page's end. */
function pageBottom(element: HTMLElement): number {
  const page = element.closest("main");
  return page !== null ? parseFloat(getComputedStyle(page).paddingBlockEnd) || 0 : 0;
}

/**
 * The grid's height when the page does not scroll: from its top to the
 * window's bottom edge, less the page's bottom padding — measured in the
 * page's coordinates, as the feed is.
 */
function gridHeight(grid: HTMLElement): number {
  const room =
    document.documentElement.clientHeight - (grid.getBoundingClientRect().top + window.scrollY) - pageBottom(grid);
  return Math.round(Math.max(GRID_MIN_REM * rem(), room));
}

/**
 * Which end hides entries — a fade there and only there; a pixel of rounding
 * is not „more". The fact sheet's scroller says the same (UX-Gutachten 2, B-8).
 */
export function markEnds(feed: HTMLElement): void {
  feed.toggleAttribute("data-more-start", feed.scrollTop > 1);
  feed.toggleAttribute("data-more-end", feed.scrollTop + feed.clientHeight < feed.scrollHeight - 1);
}

/**
 * A scroller holding nothing a Tab can reach — a record of system lines only
 * — cannot be scrolled by the keyboard (WCAG 2.1.1): then, and only while it
 * does scroll, it is a stop of its own with a name. Written to the element,
 * not rendered: it depends on what the rows drew, which React does not know
 * until they are in the page. What a closed fold holds is out of reach
 * (`inert`) and does not count.
 */
function markReach(feed: HTMLElement): void {
  const reachable = [...feed.querySelectorAll(FOCUSABLE)].some((element) => element.closest("[inert]") === null);
  const scrolls = feed.scrollHeight > feed.clientHeight + 1;
  if (!reachable && scrolls) {
    feed.tabIndex = 0;
    feed.setAttribute("role", "region");
    feed.setAttribute("aria-label", "Verlauf");
    return;
  }
  feed.removeAttribute("tabindex");
  feed.removeAttribute("role");
  feed.removeAttribute("aria-label");
}

/**
 * From `FIXED_FROM_PX` of window height on, the grid holds the window's
 * height (`data-fest`, `--akte-raster`) and the feed fills its panel; below,
 * the feed is capped at the room it has (`--akte-hoehe`) and the page
 * scrolls — and so it does where the feed would get less than its floor
 * (`feedFits`). Only while a feed stands: on the drawings or the Stände the
 * grid is as tall as what it holds again.
 */
/**
 * Whether the panel's other parts — tabs, tools, filters, an intake result, a
 * note being written — leave the feed its floor in the grid's height. Their
 * height is what the panel holds less the feed, whichever way the grid
 * stands: in the window the feed takes the rest, out of it the feed is
 * capped — neither changes the rest, so deciding never moves what decides.
 * What the panel holds is its content's height (`scrollHeight`), not its
 * box: held in the window with the feed at its floor, the content runs out
 * of the box, and the box alone would say it fits.
 */
function feedFits(feed: HTMLElement, grid: HTMLElement): boolean {
  const panel = feed.closest(`.${styles.record}`);
  if (panel === null) return true;
  const holds = Math.max(panel.getBoundingClientRect().height, panel.scrollHeight);
  const others = holds - feed.getBoundingClientRect().height;
  return gridHeight(grid) - others >= FEED_FLOOR_REM * rem();
}

function settle(feed: HTMLElement): void {
  const grid = feed.closest<HTMLElement>(`.${styles.grid}`);
  const fixed = grid !== null && document.documentElement.clientHeight >= FIXED_FROM_PX && feedFits(feed, grid);
  if (grid !== null) {
    grid.toggleAttribute("data-fest", fixed);
    const raster = fixed ? `${gridHeight(grid)}px` : "";
    if (grid.style.getPropertyValue("--akte-raster") !== raster) grid.style.setProperty("--akte-raster", raster);
  }
  const height = `${feedHeight(feed)}px`;
  if (feed.style.getPropertyValue("--akte-hoehe") !== height) feed.style.setProperty("--akte-hoehe", height);
  markEnds(feed);
  markReach(feed);
}

/** Settles the feed in the next frame — once, however many changes asked for it in this one. */
function settler(feed: HTMLElement): { later: () => void; stop: () => void } {
  let frame = 0;
  return {
    later: () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => settle(feed));
    },
    stop: () => window.cancelAnimationFrame(frame),
  };
}

/**
 * Sets the feed's height (`--akte-hoehe`), its fading ends and its reach —
 * not after every render of the record tab (final review, M-2): a note typed
 * there re-renders it on every key, and every settle is a forced layout and a
 * walk over the entries. Now when what the feed lists changes (`listed`: the
 * entries shown, the filter, a note opened or closed — before the paint),
 * when the feed or what it holds changes size (an entry came, a fold opened;
 * observed, so no render is needed), on a resize of the window, and when the
 * page around it changes height without the tab drawing again (a notice in
 * the head put away): the work area (`main`, else the record screen) is
 * observed — not the body, which is as tall as the window whatever it holds.
 * An observed change is written in the next frame so it does not land inside
 * the observer's own delivery; the feed's own new height changes the area
 * once more, and the measure after it finds what it wrote. At 900 px and
 * less the stylesheet ignores both heights — the page scrolls then.
 */
function useFeed(ref: RefObject<HTMLElement | null>, listed: string): void {
  useLayoutEffect(() => {
    if (ref.current !== null) settle(ref.current);
  }, [ref, listed]);

  useEffect(() => {
    const feed = ref.current;
    if (feed === null) return undefined;
    const { later, stop } = settler(feed);
    window.addEventListener("resize", later);
    const area = feed.closest("main") ?? feed.closest(`.${styles.screen}`);
    const observer = area === null || typeof ResizeObserver === "undefined" ? null : new ResizeObserver(later);
    if (area !== null) observer?.observe(area);
    const grid = feed.closest<HTMLElement>(`.${styles.grid}`);
    return () => {
      stop();
      window.removeEventListener("resize", later);
      observer?.disconnect();
      // No feed, no height to hold: the grid is as tall as what it holds.
      grid?.removeAttribute("data-fest");
      grid?.style.removeProperty("--akte-raster");
    };
  }, [ref]);

  // The feed's own box and what it holds — subscribed again when the list
  // changes, so a day that came is observed too.
  useEffect(() => {
    const feed = ref.current;
    if (feed === null || typeof ResizeObserver === "undefined") return undefined;
    const { later, stop } = settler(feed);
    const observer = new ResizeObserver(later);
    observer.observe(feed);
    for (const child of feed.children) observer.observe(child);
    return () => {
      stop();
      observer.disconnect();
    };
  }, [ref, listed]);
}

/**
 * The record's timeline in a surface of its own (design 2026-10-24 §6.1):
 * the page no longer grows with the record. It scrolls in itself, the day
 * heads stand at its top, and the ends that hide entries fade. `listed` names
 * what it lists — it changes when the entries shown, the filter or the panel
 * above it change, and the feed is settled again before the paint.
 */
export function Feed({ children, listed }: { children: ReactNode; listed: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useFeed(ref, listed);
  return (
    <div ref={ref} className={styles.feed} onScroll={(event) => markEnds(event.currentTarget)}>
      {children}
    </div>
  );
}
